class RoRedirectPopup extends HTMLElement {
  static COOKIE_VERSION = 'v1';
  static LONG_COOKIE_DURATION = '60d';
  static SHORT_COOKIE_DURATION = '16h';

  constructor() {
    super();
    this.originCountry = null;
    this.originCountryMarket = null;
  }

  get originCountryCookieHandle() {
    return `origin_country-${RoRedirectPopup.COOKIE_VERSION}`;
  }

  get selectedCountryCookieHandle() {
    return `selected_country-${RoRedirectPopup.COOKIE_VERSION}`;
  }

  get closedPopupCookieHandle() {
    return `closed_redirect_popup-${RoRedirectPopup.COOKIE_VERSION}`;
  }

  get cookieDomain() {
    return window.location.hostname;
  }

  connectedCallback() {
    this.dialogElement = this.querySelector('dialog#redirectPopup');
    this.form = this.querySelector('#RedirectPopupLocalizationForm');
    this.countryInput = this.querySelector('#redirectPopupCountryCode');
    this.localizationCountryBtn = this.querySelector('#localizationCountryBtn');
    this.originCountryBtn = this.querySelector('#originCountryBtn');
    this.availableCountries = window.RICKOWENS?.redirectPopup?.availableCountries ?? [];
    this.localizationMarket = window.RICKOWENS?.redirectPopup?.localizationMarket ?? '';

    this.performMarketCheck();
  }

  performMarketCheck() {
    if (this.getCookie(this.selectedCountryCookieHandle) || this.getCookie(this.closedPopupCookieHandle)) {
      return;
    }

    this.geolocalizeUser().then(() => {
      if (this.originCountryMarket == this.localizationMarket) {
        this.setCookie(
          this.selectedCountryCookieHandle,
          this.originCountry,
          RoRedirectPopup.LONG_COOKIE_DURATION,
          this.cookieDomain
        );
        return;
      }

      !this.getCookie(this.closedPopupCookieHandle) && this.showPopup();
    });
  }

  async geolocalizeUser() {
    if (this.originCountry && this.originCountryMarket) return;

    const originCountryCookie = this.getCookie(this.originCountryCookieHandle);

    if (originCountryCookie) {
      this.originCountry = originCountryCookie;
      this.originCountryMarket = this.marketFromISO(this.originCountry);
      return;
    }

    const response = await fetch('/browsing_context_suggestions.json');
    const data = await response.json();

    this.originCountry = data.detected_values.country.handle;
    this.setCookie(
      this.originCountryCookieHandle,
      this.originCountry,
      RoRedirectPopup.LONG_COOKIE_DURATION,
      this.cookieDomain
    );
    this.originCountryMarket = this.marketFromISO(this.originCountry);
  }

  marketFromISO(isoCode) {
    return this.availableCountries.find((country) => country.isocode == isoCode)?.market;
  }

  showPopup() {
    document.querySelector('body').style.overflow = 'hidden';

    this.localizationCountryBtn.addEventListener('click', this.continueToLocalizationCountry);
    this.originCountryBtn.addEventListener('click', this.stayInOriginCountry);
    this.dialogElement.addEventListener('keydown', this.handleEscapeKey);

    const originCountryName = this.availableCountries.find(
      (country) => country.isocode === this.originCountry
    )?.name;
    const originCountryDescSpan = this.querySelector('#originCountryDescSpan');
    const originCountryBtnSpan = this.querySelector('#originCountryBtnSpan');

    originCountryDescSpan && (originCountryDescSpan.textContent = originCountryName);
    originCountryBtnSpan && (originCountryBtnSpan.textContent = this.originCountry);

    this.dialogElement.showModal();
  }

  handleEscapeKey = (e) => {
    e.key === 'Escape' && e.preventDefault();
  };

  continueToLocalizationCountry = () => {
    const localizationCountry = this.getCookie('localization');

    this.setCookie(
      this.selectedCountryCookieHandle,
      localizationCountry || this.countryInput.value,
      RoRedirectPopup.LONG_COOKIE_DURATION,
      this.cookieDomain
    );
    this.hidePopup();
  };

  stayInOriginCountry = () => {
    if (!this.originCountry) return;

    this.setCookie(
      this.selectedCountryCookieHandle,
      this.originCountry,
      RoRedirectPopup.LONG_COOKIE_DURATION,
      this.cookieDomain
    );
    this.setCookie(
      this.closedPopupCookieHandle,
      'true',
      RoRedirectPopup.SHORT_COOKIE_DURATION,
      this.cookieDomain
    );
    this.countryInput.value = this.originCountry;
    this.form.requestSubmit();
  };

  hidePopup() {
    document.querySelector('body').style.overflow = 'auto';
    this.dialogElement.close();

    this.localizationCountryBtn.removeEventListener('click', this.continueToLocalizationCountry);
    this.originCountryBtn.removeEventListener('click', this.stayInOriginCountry);
    this.dialogElement.removeEventListener('keydown', this.handleEscapeKey);

    this.setCookie(
      this.closedPopupCookieHandle,
      'true',
      RoRedirectPopup.SHORT_COOKIE_DURATION,
      this.cookieDomain
    );
  }

  getCookie(handle) {
    return document.cookie.match(new RegExp(`(^| )${handle}=([^;]+)`))?.at(2);
  }

  setCookie(name, value, durationString, domain) {
    const expiresIn = this.parseDuration(durationString);

    if (!expiresIn || !value) return;

    const d = new Date();

    d.setTime(d.getTime() + expiresIn);
    document.cookie =
      name + '=' + value + '; expires=' + d.toUTCString() + '; Domain=' + domain + '; secure; path=/';
  }

  parseDuration(durationString) {
    const match = durationString.match(/(\d+)([hdmy])/);

    if (!match) return null;

    const value = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();
    const multipliers = {
      h: 60 * 60 * 1000,
      d: 24 * 60 * 60 * 1000,
      m: 30 * 24 * 60 * 60 * 1000,
      y: 365 * 24 * 60 * 60 * 1000,
    };

    return multipliers[unit] ? value * multipliers[unit] : null;
  }
}

customElements.define('ro-redirect-popup', RoRedirectPopup);

class RoCountrySelector extends HTMLElement {
  static COOKIE_VERSION = 'v1';
  static COOKIE_MAX_AGE = 60 * 24 * 60 * 60;

  connectedCallback() {
    this.dialogElement = this.querySelector('dialog');
    this.openDialogBtn = this.querySelector('button[id$="OpenRoCountrySelector"]');
    this.closeDialogBtn = this.querySelector('button[id$="CloseRoCountrySelector"]');
    this.countrySelect = this.querySelector('select[name="country_code"]');
    this.form = this.querySelector('form');

    this.openDialogBtn.addEventListener('click', this.showPopup.bind(this));
    this.closeDialogBtn.addEventListener('click', this.hidePopup.bind(this));
    this.dialogElement.addEventListener('keydown', this.handleEscapeKey.bind(this));
    this.form && this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  onSubmit() {
    const countryCode = this.countrySelect?.value;

    if (!countryCode) return;

    const domain = window.location.hostname;
    const maxAge = RoCountrySelector.COOKIE_MAX_AGE;

    document.cookie = `closed_redirect_popup-${RoCountrySelector.COOKIE_VERSION}=true; max-age=${maxAge}; path=/; secure; domain=${domain}`;
    document.cookie = `selected_country-${RoCountrySelector.COOKIE_VERSION}=${countryCode}; max-age=${maxAge}; path=/; secure; domain=${domain}`;
  }

  showPopup() {
    document.querySelector('body').style.overflow = 'hidden';
    this.dialogElement.showModal();
  }

  hidePopup() {
    document.querySelector('body').style.overflow = 'auto';
    this.dialogElement.close();
  }

  handleEscapeKey(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.hidePopup();
    }
  }
}

customElements.define('ro-country-selector', RoCountrySelector);
